import React from 'react'
import { Link, useParams } from 'react-router'
import { useUser, useUsersWithActivity } from '@klear/ui/hooks/use-user'
import { useTrader } from '@klear/ui/hooks/use-trader'
import { useEmailMutations } from '@klear/ui/hooks/use-email'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { PERMISSION_ACTION_EDIT, PERMISSION_ACTION_WELCOME_EMAIL, PERMISSION_ENTITY_USER } from '@klear/constants'
import type { UserRole } from '@klear/user/user-role'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

const UserView: React.FC = () => {
  const { userId } = useParams()
  const { user: currentUser } = useAuth()

  const id = parseInt(userId || '0')
  const { user: userData, isLoading, error } = useUser(id)
  const { users: usersWithActivity } = useUsersWithActivity()
  const userWithActivity = usersWithActivity?.find((u) => u.id === id)
  const { trader: traderData } = useTrader(userData?.roles?.[0]?.traderId?.toString() || '')
  useDocumentTitle(
    userData && [userData.firstName, userData.lastName].filter(Boolean).join(' '),
    'User',
    traderData?.name
  )

  const { sendWelcomeEmail, isSendingWelcomeEmail } = useEmailMutations()

  const handleResendInvitation = async () => {
    if (userData?.id) {
      await sendWelcomeEmail(userData.id)
    }
  }

  const canEditUser = () => {
    if (!userData || !currentUser) return false
    if (userData.roles?.some((r: UserRole) => r.role.startsWith('klear_'))) {
      return currentUser.isSuperAdmin
    }
    return currentUser.hasPermission(PERMISSION_ENTITY_USER, PERMISSION_ACTION_EDIT)
  }

  if (isLoading) {
    return <div className="p-6 text-center">Loading user details...</div>
  }

  if (error) {
    return <div className="p-6 text-center text-red-600">Error: {error.message}</div>
  }

  if (!userData) {
    return <div className="p-6 text-center">User not found</div>
  }

  const getTraderName = () => {
    if (traderData) {
      return traderData.name || 'Unknown'
    } else if (userData && !userData.roles?.[0]?.traderId) {
      return 'N/A'
    }
  }

  return (
    <div className="p-6">
      <div className="bg-background rounded-lg shadow-md">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-semibold text-foreground">User Details</h1>
            <div className="flex space-x-4">
              <Link to="/user" className="px-4 py-2 bg-muted text-foreground rounded-md hover:bg-muted/70">
                Back to List
              </Link>

              {canEditUser() && (
                <>
                  <Link
                    to={`/user/${userData.id}/edit`}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Edit
                  </Link>

                  {currentUser?.hasPermission(PERMISSION_ENTITY_USER, PERMISSION_ACTION_WELCOME_EMAIL) && (
                    <button
                      onClick={handleResendInvitation}
                      disabled={isSendingWelcomeEmail}
                      className="border border-blue-600 text-blue-600 hover:bg-blue-50 py-2 px-4 rounded disabled:opacity-50"
                    >
                      {isSendingWelcomeEmail ? 'Sending...' : 'Send Welcome Email'}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <section>
            <h2 className="text-lg font-medium text-foreground mb-4">User Information</h2>
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-muted-foreground">Email:</span>
                <div className="mt-1">{userData.email}</div>
              </div>
              <div>
                <span className="text-sm font-medium text-muted-foreground">Name:</span>
                <div className="mt-1">
                  {[userData.firstName, userData.middleName, userData.lastName].filter(Boolean).join(' ')}
                </div>
              </div>
              <div>
                <span className="text-sm font-medium text-muted-foreground">Phone:</span>
                <div className="mt-1">{userData.phoneNumber || 'N/A'}</div>
              </div>
              <div>
                <span className="text-sm font-medium text-muted-foreground">Job Title:</span>
                <div className="mt-1">{userData.jobTitle || 'N/A'}</div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-medium text-foreground mb-4">Role Information</h2>
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-muted-foreground">Organization:</span>
                <div className="mt-1">{getTraderName()}</div>
              </div>
              <div>
                <span className="text-sm font-medium text-muted-foreground">Role:</span>
                <div className="mt-1">
                  {userData.roles?.map((role: UserRole) => {
                    const roleDisplay = role.role
                      .replace('klear_', 'Klear ')
                      .replace('trader_', 'Trader ')
                      .replace('_', ' ')
                      .split(' ')
                      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(' ')

                    return <div key={`${role.traderId}-${role.role}`}>{roleDisplay}</div>
                  })}
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-medium text-foreground mb-4">System Information</h2>
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-muted-foreground">User ID:</span>
                <div className="mt-1">{userData.id}</div>
              </div>
              <div>
                <span className="text-sm font-medium text-muted-foreground">Created:</span>
                <div className="mt-1">{userData.createdAt.toKlearDate().toString()}</div>
              </div>
              <div>
                <span className="text-sm font-medium text-muted-foreground">Last Login:</span>
                <div className="mt-1">{userData.lastLoginTime?.toKlearDate().toString() ?? 'Never'}</div>
              </div>
            </div>
          </section>

          {userWithActivity && (
            <section>
              <h2 className="text-lg font-medium text-foreground mb-4">Legal Acceptance</h2>
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-muted-foreground">Terms of Service:</span>
                  <div className="mt-1">
                    {userWithActivity.termsOfServiceAccepted ? (
                      <span className="text-green-600">Accepted</span>
                    ) : (
                      <span className="text-amber-600">Not accepted</span>
                    )}
                  </div>
                </div>
                <div>
                  <span className="text-sm font-medium text-muted-foreground">Privacy Policy:</span>
                  <div className="mt-1">
                    {userWithActivity.privacyPolicyAccepted ? (
                      <span className="text-green-600">Accepted</span>
                    ) : (
                      <span className="text-amber-600">Not accepted</span>
                    )}
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  )
}

export default UserView
